// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import InstrumentHolidaysScreenTemplate from '@front/templates/InstrumentHolidaysScreenTemplate.html';
import { Resources } from '@shared/localizations/Resources';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { HolidayItem } from '../cache/HolidayItem';
import { Control } from '../elements/Control';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { Rectangle } from '@shared/commons/Geometry';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { DayType } from '@shared/utils/Session/DayType';
import { ControlsUtils } from '../UtilsClasses/ControlsUtils';
export class InstrumentHolidaysScreen extends ApplicationPanelWithTable {
    constructor() {
        super();
    }
    getType() {
        return PanelNames.InstrumentHolidaysScreen;
    }
    updatePanelHeader() { }
    oninit() {
        super.oninit();
        this.observe('visible', this.onVisibleChanged);
        this.observe('instrument', this.onInstrumentChanged, { init: false });
        this.updateBounds = this.updateBounds.bind(this);
        this._resizeObserver = new ResizeObserver(this.updateBounds);
    }
    oncomplete() {
        super.oncomplete();
        this.populateHolidayList();
        this.updateBounds();
    }
    onVisibleChanged(visible) {
        const el = this.find('*');
        if (visible) {
            Control.Ticker.Subscribe(this.onControlGlobalTick, this);
            this._resizeObserver.observe(el);
        }
        else {
            Control.Ticker.UnSubscribe(this.onControlGlobalTick, this);
            this._resizeObserver.unobserve(el);
        }
    }
    onControlGlobalTick() {
        this.TickAsync();
    }
    lostFocus() {
        super.lostFocus();
        void this.set('visible', false);
    }
    onCloseButtonClick() {
        void this.set('visible', false);
    }
    static show(instrument, x, y) {
        const scr = InstrumentHolidaysScreen.getInstance();
        scr.themeChange();
        scr.localize();
        scr.setLocation(x, y);
        void scr.set({
            header: Resources.getResource('InstrumentDetailsPanel.Holidays list'),
            instrument
        });
        void scr.set('visible', true);
        scr.setFocus();
    }
    onInstrumentChanged() {
        this.populateHolidayList();
        this.updateBounds();
    }
    populateHolidayList() {
        const scr = InstrumentHolidaysScreen.getInstance();
        const instrument = scr.get('instrument');
        if (!isNullOrUndefined(scr.quickTableRactive)) {
            scr.quickTableRactive.quickTable.ClearAll();
        }
        const ts = instrument === null || instrument === void 0 ? void 0 : instrument.Session;
        if (isNullOrUndefined(ts)) {
            return;
        }
        const holidaysList = ts.getSortedHolidays();
        for (const holiday of holidaysList) {
            if (ts.isFutureHoliday(holiday)) {
                const formatDate = holiday.GetFormattedDate();
                const schedule = InstrumentHolidaysScreen.holidayTypeToShedule(holiday.holidayType);
                if (!isNullOrUndefined(scr.quickTableRactive)) {
                    scr.quickTableRactive.quickTable.AddItem(new HolidayItem(formatDate, holiday.name, schedule));
                }
            }
        }
    }
    updateBounds() {
        this.updateHeight();
        this.quickTableRactive.setSizes();
        const newLocation = ControlsUtils.CorrectPopupLocation(new Rectangle(this.get('left'), this.get('top'), this.get('width'), this.get('height')));
        this.setLocation(newLocation.newX, newLocation.newY);
    }
    updateHeight() {
        var _a, _b;
        const tableEl = this.find('.application-panel-container-table');
        if (isNullOrUndefined((_a = this.quickTableRactive) === null || _a === void 0 ? void 0 : _a.quickTable) || isNullOrUndefined(tableEl)) {
            return;
        }
        const maxElementsOnScreen = 6;
        const rowsCount = this.quickTableRactive.quickTable.rowsArray.length;
        const newTableHeight = this.quickTableRactive.quickTable.getTableHeight(Math.min(rowsCount, maxElementsOnScreen));
        const el = this.find('*');
        const elRect = el.getBoundingClientRect();
        const tableElRect = tableEl === null || tableEl === void 0 ? void 0 : tableEl.getBoundingClientRect();
        const oldTableHeight = (_b = tableElRect === null || tableElRect === void 0 ? void 0 : tableElRect.height) !== null && _b !== void 0 ? _b : 0;
        void this.set('height', elRect.height - oldTableHeight + newTableHeight);
    }
    static holidayTypeToShedule(holidayType) {
        switch (holidayType) {
            case DayType.NOT_WORKING:
                return Resources.getResource('InstrumentDetailsPanel.non trading day');
            case DayType.SHORTED:
                return Resources.getResource('InstrumentDetailsPanel.shortened');
            case DayType.WORKING:
            default:
                return Resources.getResource('InstrumentDetailsPanel.working');
        }
    }
    dispose() {
        super.dispose();
        Control.Ticker.UnSubscribe(this.onControlGlobalTick, this);
        InstrumentHolidaysScreen.instance = null;
        this._resizeObserver.disconnect();
    }
    static getInstance() {
        if (!isNullOrUndefined(InstrumentHolidaysScreen.instance)) {
            return InstrumentHolidaysScreen.instance;
        }
        const scr = new InstrumentHolidaysScreen();
        void scr.set('visible', false);
        MainWindowManager.AddControl(scr);
        InstrumentHolidaysScreen.instance = scr;
        return InstrumentHolidaysScreen.instance;
    }
}
InstrumentHolidaysScreen.instance = null;
ApplicationPanelWithTable.extendWith(InstrumentHolidaysScreen, {
    data: function () {
        return {
            showHeader: true,
            showFooter: false,
            movable: false,
            resizable: false,
            dockablePanel: false,
            attached: false,
            width: 500,
            height: 0,
            instrument: null,
            style_addition_jsWin: 'instrument-holidays-screen'
        };
    },
    partials: {
        bodyPartial: InstrumentHolidaysScreenTemplate
    }
});
